import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';

// Styles
import './assets/css/style.css';

// Third-Party
import axios from 'axios';
import VueProgressBar from 'vue-progressbar';
import Notifications from 'vue-notification'
import VTooltip from 'v-tooltip';
import VueTheMask from 'vue-the-mask'
import ToggleButton from 'vue-js-toggle-button'
import VueScrollTo from 'vue-scrollto';
import VueTagsInput from '@johmun/vue-tags-input';
import Spinner from 'vue-simple-spinner';
import { Loader } from '@googlemaps/js-api-loader';
import Sortable from 'vue-sortable';
import wysiwyg from "vue-wysiwyg";
import HighchartsVue from 'highcharts-vue';

import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fal } from '@fortawesome/pro-light-svg-icons'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { fab } from '@fortawesome/free-brands-svg-icons'
library.add(fal, fas, fab);

// VeeValidate
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';

// Custom Components
import PageHeader from '@/views/layout/PageHeader';
import ProTable from '@/components/pro-table/Table';
import Tabs from '@/views/layout/Tabs';
import InputText from "@/components/forms/InputText";
import InputRadio from "@/components/forms/InputRadio";
import InputTextarea from "@/components/forms/InputTextarea";
import InputSelect from "@/components/forms/InputSelect";
import InputToggle from "@/components/forms/InputToggle";
import InputTags from "@/components/forms/InputTags";
import InputDateTime from "@/components/forms/InputDateTime";
import InputAddressAutocomplete from "@/components/forms/InputAddressAutocomplete";
import Editor from "@/components/forms/Editor";
import FileUpload from "@/components/forms/FileUpload";
import InputColor from "@/components/forms/InputColor";

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('PageHeader', PageHeader);
Vue.component('ProTable', ProTable);
Vue.component('Tabs', Tabs);
Vue.component('InputText', InputText);
Vue.component('InputRadio', InputRadio);
Vue.component('InputTextarea', InputTextarea);
Vue.component('InputSelect', InputSelect);
Vue.component('InputToggle', InputToggle);
Vue.component('InputTags', InputTags);
Vue.component('InputAddressAutocomplete', InputAddressAutocomplete);
Vue.component('VueTagsInput', VueTagsInput);
Vue.component('Spinner', Spinner);
Vue.component('InputDateTime', InputDateTime);
Vue.component('Editor', Editor);
Vue.component('FileUpload', FileUpload);
Vue.component('InputColor', InputColor);

Vue.use(Notifications);
Vue.use(VueScrollTo);
Vue.use(VTooltip);
Vue.use(VueTheMask);
Vue.use(ToggleButton);
Vue.use(require('vue-moment'));
Vue.use(Sortable);
Vue.use(HighchartsVue);
Vue.use(wysiwyg, {
    hideModules: {
        "image": true,
        "code": true
    },
    maxHeight: "500px"
});

Vue.use(VueProgressBar, {
    color: '#4995D1',
    failedColor: 'red',
    height: '5px'
});

extend('email', {
    ...email,
    message: '{_field_} is not a valid email address'
});

extend('required', {
    ...required,
    message: '{_field_} is required'
});

extend('password', {
    params: ['target'],
    validate(value, { target }) {
        return value === target;
    },
    message: 'Password confirmation does not match'
});

Vue.config.productionTip = false;

// Axios
if (localStorage.getItem(process.env.VUE_APP_LOCALSTORAGE_KEY)){
    axios.defaults.headers.common['Authorization'] = 'Bearer '+ JSON.parse(localStorage.getItem(process.env.VUE_APP_LOCALSTORAGE_KEY)).user.token;
}

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults['xsrfCookieName'] = null;

// Handle HTTP Requests
axios.interceptors.request.use((config) => {
    if (!router.currentRoute.meta.public) {
        config.headers.common['Authorization'] = 'Bearer ' + JSON.parse(localStorage.getItem(process.env.VUE_APP_LOCALSTORAGE_KEY))?.user?.token;
    }
    return config;
}, function (error) {
    return Promise.reject(error);
});

// Handle HTTP Responses
axios.interceptors.response.use(response => {
    return response;
}, function (error) {
    if (error.response?.status === 401 && error.response?.data?.message === 'Unauthenticated.'){
        store.dispatch('AUTH_LOGOUT', error).then(response => router.push('login'));
    } else {
        return Promise.reject(error);
    }
});

router.beforeEach((to, from, next) => {
    if (to.meta.public){
        document.title = to.meta.title;
    } else {
        document.title = to.meta.title + ' | ' + process.env.VUE_APP_NAME + ' Admin';
    }

    // Perform Redirect
    if (store.getters.redirect !== null){
        let redirect = store.getters.redirect;
        store.commit('SET_REDIRECT', null);
        next(redirect);
    } else if (to.query.redirect){
        store.commit('SET_REDIRECT', from.fullPath);
    }

    if (to.meta.public){
        next();
    } else {
        if (to.name === 'reset-password'){
            next();
        } else if (store.getters.token === '' && to.name !== 'login'){
            next('login');
        } else if (store.getters.token !== '' && to.name === 'login'){
            next('dashboard');
        } else {
            if (to.meta.guard && store.getters.sections.indexOf(to.path) === -1){
                next('/permission-error');
            } else {
                next();
            }
        }
    }
});

router.onError((error) => {
    const chunkLoadFailedRegex = /Loading chunk \d+ failed|Failed to fetch dynamically imported module/

    if (chunkLoadFailedRegex.test(error.message)) {
        console.error('Dynamic import failed. Reloading...')
        window.location.href = window.location.href + '?v=' + Date.now()
    }
})

window.loader = new Loader({
    apiKey: "AIzaSyADJ7Lt2v6r5zyX0FHSOmvyPSLYotTfG-Y",
    version: "3.40",
    libraries: ['geometry', 'places'],
    region: 'AU'
});

Vue.config.devtools = true;

new Vue({
    router,
    store,
    created(){
        return this.$store.dispatch('AUTH_CONFIRM').then((error) => {
            if (error){
                router.push('/login');
            }
        });
    },
    render: (h) => h(App),
}).$mount('#app');

window.Vapor = require('laravel-vapor');
